/* eslint-disable react/display-name */
import React, { forwardRef, CSSProperties, ReactNode, Ref, ReactElement } from 'react';
import { useTheme } from '@mui/material/styles';
import Card, { CardProps } from '@mui/material/Card';
import CardContent, { CardContentProps } from '@mui/material/CardContent';
import CardHeader, { CardHeaderProps } from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { KeyedObject } from './Components/root';

const headerSX = { p: 2.5, '& .MuiCardHeader-action': { m: '0px auto', alignSelf: 'center' } };

export interface MainCardProps extends KeyedObject {
  border?: boolean;
  boxShadow?: boolean;
  children: ReactNode;
  subheader?: ReactNode;
  style?: CSSProperties;
  content?: boolean;
  contentSX?: CardContentProps['sx'];
  darkTitle?: boolean;
  divider?: boolean;
  sx?: CardProps['sx'];
  secondary?: CardHeaderProps['action'];
  shadow?: string;
  elevation?: number;
  title?: ReactNode;
  codeHighlight?: boolean;
  codeString?: string;
  modal?: boolean;
  borderRadius:String;
}

const MainCard = forwardRef<HTMLDivElement, MainCardProps>(
	(
		{
			border = true,
			boxShadow = true,
			children,
			subheader,
			content = true,
			contentSX = {},
			darkTitle,
			divider = true,
			elevation,
			secondary,
			shadow,
			sx = {},
			title,
			codeHighlight = false,
			codeString,
			borderRadius,
			modal = false,
			...others
		},
		ref
	) =>
	{
		const theme = useTheme();

		return (
			<Card
				elevation={elevation || 0}
				ref={ref}
				{...others}
				sx={{
					position     : 'relative',
					border       : border ? '1px solid' : 'none',
					borderRadius : borderRadius || 3.2,
					borderColor  : theme.palette.divider,
					...(codeHighlight && {
						'& pre' : {
							m          : 0,
							p          : 'var(--pixel12) !important',
							fontFamily : theme.typography.fontFamily,
							fontSize   : '0.75rem'
						}
					}),
					...(modal && {
						position                 : 'absolute' as 'absolute',
						top                      : 'var(--percent50)',
						left                     : 'var(--percent50)',
						transform                : 'translate(-50%, -50%)',
						width                    : { xs: 'calc( 100% - 50px)', sm: 'auto' },
						'& .MuiCardContent-root' : {
							overflowY : 'auto',
							minHeight : 'auto',
							maxHeight : 'calc(100vh - 200px)'
						}
					}),
					...sx
				}}
			>
				{!darkTitle && title && (
					<CardHeader
						sx={headerSX}
						titleTypographyProps={{ variant: 'subtitle1', sx: { fontWeight: 500 } }}
						title={title}
						action={secondary}
						subheader={subheader}
					/>
				)}
				{darkTitle && title && (
					<CardHeader
						sx={headerSX}
						title={<Typography variant='h4' sx={{ fontWeight: 500 }}>{title}</Typography>}
						action={secondary}
					/>
				)}

				{title && divider && <Divider />}

				{content && <CardContent sx={contentSX}>{children}</CardContent>}
				{!content && children}

				{codeString && (
					<>
						<Divider sx={{ borderStyle: 'dashed' }} />
					</>
				)}
			</Card>
		);
	}
);

export default MainCard;